import React, { useState, useEffect } from "react";
import { Table, Button, Form, Modal, Row, Col, Badge, Card } from "react-bootstrap";
import rulesData from "../data/001.01 CTIS_Rules_Checklist.json";
import { validateItem } from "../../validators/CTISItemValidator";

export default function CTISItemRegisterPage() {
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [currentItem, setCurrentItem] = useState({
    id: "",
    ctis_section: "PART_I",
    item_name: "",
    item_type: "document",
    mandatory: true,
    publishable: true,
    public_version_required: true,
    restricted_version_allowed: true,
    per_msc: false,
    source_chapter: "",
    status: "active"
  });

  // Relationship Awareness: Sync ID prefix whenever Section changes
  useEffect(() => {
    // Only auto-prefix if we are in "creation mode" or switching sections
    // This logic ensures the Section selection dictates the initial ID structure
    if (currentItem.id.length < 15 || !currentItem.id.includes(currentItem.ctis_section)) {
      const prefix = `001.01 CTIS_${currentItem.ctis_section}_`;
      setCurrentItem(prev => ({ ...prev, id: prefix }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem.ctis_section]); 

  const handleSave = () => {
    const result = validateItem(currentItem, items);
    if (!result.isValid) {
      setErrors(result.errors);
      return;
    }
    setItems(prev => [...prev.filter(i => i.id !== currentItem.id), currentItem]);
    setShowModal(false);
  };

  const openAddModal = () => {
    setCurrentItem({
      id: "",
      ctis_section: "PART_I",
      item_name: "",
      item_type: "document",
      mandatory: true,
      publishable: true,
      public_version_required: true,
      restricted_version_allowed: true,
      per_msc: false,
      source_chapter: "",
      status: "active"
    });
    setErrors({});
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setCurrentItem(item);
    setErrors({});
    setShowModal(true);
  };

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h3 mb-0">CTIS Item Register</h1>
          <p className="text-muted">Master inventory governed by EMA Sponsor Handbook rules.</p>
        </div>
        <Button variant="primary" onClick={openAddModal}>+ Add Register Item</Button>
      </div>

      <Table hover responsive className="border bg-white shadow-sm">
        <thead className="table-light">
          <tr>
            <th>ID / Section</th>
            <th>Item Name</th>
            <th>Type</th>
            <th>Regulatory Logic</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map(item => (
            <tr key={item.id}>
              <td>
                <div className="text-monospace small fw-bold text-primary">{item.id}</div>
                <Badge bg="secondary">{item.ctis_section}</Badge>
              </td>
              <td><strong>{item.item_name}</strong></td>
              <td>{item.item_type}</td>
              <td>
                {item.mandatory && <Badge bg="danger" className="me-1">Mandatory</Badge>}
                {item.publishable && <Badge bg="success" className="me-1">Publishable</Badge>}
                {item.per_msc && <Badge bg="info" text="dark">Per MSC</Badge>}
              </td>
              <td>
                <Button variant="link" size="sm" onClick={() => openEditModal(item)}>Edit</Button>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan="5" className="text-center py-5 text-muted">
                Registry is empty. Use the "Add" button to define CTIS items.
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>CTIS Item Configuration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="mb-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold text-uppercase small">1. Regulatory Section</Form.Label>
                  <Form.Select 
                    value={currentItem.ctis_section}
                    onChange={e => setCurrentItem(prev => ({...prev, ctis_section: e.target.value}))}
                  >
                    {rulesData.schema_definitions.ctis_sections.map(s => (
                      <option key={s.code} value={s.code}>{s.code} - {s.description}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">Determines the ID prefix and dossier location.</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold text-uppercase small">2. System Item ID</Form.Label>
                  <Form.Control 
                    value={currentItem.id} 
                    isInvalid={!!errors.id}
                    onChange={e => setCurrentItem(prev => ({...prev, id: e.target.value.toUpperCase()}))} 
                  />
                  <Form.Control.Feedback type="invalid">{errors.id}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold text-uppercase small">Item Name</Form.Label>
                  <Form.Control 
                    value={currentItem.item_name}
                    isInvalid={!!errors.item_name}
                    placeholder="e.g. Protocol"
                    onChange={e => setCurrentItem(prev => ({...prev, item_name: e.target.value}))} 
                  />
                  <Form.Control.Feedback type="invalid">{errors.item_name}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold text-uppercase small">Item Type</Form.Label>
                  <Form.Select 
                    value={currentItem.item_type}
                    onChange={e => setCurrentItem(prev => ({...prev, item_type: e.target.value}))}
                  >
                    {rulesData.schema_definitions.item_types.map(t => (
                      <option key={t.code} value={t.code}>{t.description}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Card className="bg-light border-0 p-3">
              <h6 className="fw-bold text-uppercase small mb-3">Controls & Requirements</h6>
              <Row>
                <Col md={4}>
                  <Form.Check type="switch" label="Mandatory" checked={currentItem.mandatory} 
                    onChange={e => setCurrentItem(prev => ({...prev, mandatory: e.target.checked}))} />
                </Col>
                <Col md={4}>
                  <Form.Check type="switch" label="Publishable" checked={currentItem.publishable} 
                    onChange={e => setCurrentItem(prev => ({...prev, publishable: e.target.checked}))} />
                </Col>
                <Col md={4}>
                  <Form.Check type="switch" label="Member State Specific" checked={currentItem.per_msc} 
                    onChange={e => setCurrentItem(prev => ({...prev, per_msc: e.target.checked}))} />
                </Col>
              </Row>
            </Card>
          </Form>
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSave}>Save to Registry</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}